import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    ArrowLeft,
    CheckCircle2,
    Clock,
    ExternalLink,
    ShieldCheck,
    Users,
    TrendingUp,
    Calendar,
} from "lucide-react";

const STATUS_STYLE = {
    Live: "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Pending Review": "bg-gold-500/10 text-gold-700 border-gold-200",
    Draft: "bg-navy-50 text-navy-500 border-navy-100",
    Rejected: "bg-red-50 text-red-700 border-red-200",
};

const fmtDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

export default function VendorProductDetail() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [signing, setSigning] = useState(false);

    const load = () => {
        setLoading(true);
        api.get(`/broker/profiles/${productId}`)
            .then((r) => setProduct(r.data))
            .catch((e) => {
                toast.error(formatApiError(e));
                navigate("/broker/products");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, [productId]);

    const signOff = async () => {
        setConfirming(false);
        setSigning(true);
        try {
            const { data } = await api.post(
                `/broker/profiles/${productId}/signoff`
            );
            setProduct(data);
            toast.success("Sign-off recorded — thank you!");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSigning(false);
        }
    };

    if (loading || !product)
        return <div className="p-10 text-navy-500">Loading…</div>;

    const saving =
        product.retail_price != null && product.actual_price != null
            ? product.retail_price - product.actual_price
            : null;
    const savingsPct =
        saving && product.retail_price
            ? Math.round((saving / product.retail_price) * 100)
            : null;
    const isLive = product.status === "Live";
    const signed = !!product.vendor_signed_off_at;

    return (
        <>
            <div className="px-6 lg:px-12 pt-6">
                <Link
                    to="/broker/products"
                    data-testid="vendor-product-back"
                    className="inline-flex items-center gap-2 text-sm text-navy-500 hover:text-navy-700"
                >
                    <ArrowLeft size={15} /> Back to my products
                </Link>
            </div>

            <PageHeader
                eyebrow={`Product · ${product.id}`}
                title={product.product}
                description={`${product.vendor} · Submitted ${
                    fmtDate(product.submitted_at) || "—"
                }`}
                testid="vendor-product-detail-header"
                action={
                    <Badge
                        data-testid="vendor-product-status-badge"
                        className={`${STATUS_STYLE[product.status]} border text-xs px-3 py-1`}
                    >
                        {product.status}
                    </Badge>
                }
            />

            <div className="px-6 lg:px-12 py-8 grid lg:grid-cols-[1fr_380px] gap-8 items-start">
                {/* MAIN - product page preview */}
                <div className="rounded-2xl bg-white shadow-soft overflow-hidden">
                    <div
                        className="h-56 flex items-center justify-center"
                        style={{ background: "linear-gradient(135deg, #f7f4ed 0%, #ffffff 100%)" }}
                    >
                        <img
                            src="/panda/panda-checklist.png"
                            alt=""
                            className="h-44 object-contain"
                        />
                    </div>
                    <div className="p-8 space-y-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                                {product.category || "General"} · {product.vendor}
                            </div>
                            <h2 className="font-display text-3xl text-navy-700">
                                {product.product}
                            </h2>
                        </div>

                        {(product.retail_price != null || product.actual_price != null) && (
                            <div className="flex items-baseline gap-3">
                                <span className="font-display text-4xl text-navy-700">
                                    ${product.actual_price}
                                </span>
                                {product.retail_price > product.actual_price && (
                                    <>
                                        <span className="text-lg text-navy-500 line-through">
                                            ${product.retail_price}
                                        </span>
                                        <Badge className="bg-gold-500 text-navy-900 border-gold-200 border">
                                            Members save ${saving?.toFixed(0)} ({savingsPct}%)
                                        </Badge>
                                    </>
                                )}
                            </div>
                        )}

                        {product.description && (
                            <p className="text-navy-700 leading-relaxed">
                                {product.description}
                            </p>
                        )}

                        {product.website && (
                            <a
                                href={product.website}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="vendor-product-website"
                                className="inline-flex items-center gap-2 text-sm text-navy-700 hover:text-gold-600"
                            >
                                {product.website.replace(/^https?:\/\//, "")}
                                <ExternalLink size={14} />
                            </a>
                        )}

                        {product.admin_notes && (
                            <div className="rounded-xl bg-cream p-4 border-l-4 border-gold-500">
                                <div className="text-xs uppercase tracking-widest text-gold-700 mb-1.5">
                                    Admin review notes
                                </div>
                                <p className="text-sm text-navy-700 leading-relaxed">
                                    {product.admin_notes}
                                </p>
                            </div>
                        )}

                        {isLive && (
                            <div className="grid sm:grid-cols-3 gap-4 pt-4 border-t border-navy-100/70">
                                <Stat
                                    icon={Users}
                                    label="Members enrolled"
                                    value={product.members_enrolled?.toLocaleString() || "0"}
                                />
                                <Stat
                                    icon={TrendingUp}
                                    label="Monthly revenue"
                                    value={`$${(product.monthly_revenue || 0).toLocaleString()}`}
                                />
                                <Stat
                                    icon={Calendar}
                                    label="Conversion"
                                    value={`${product.conversion?.toFixed(1) || "0.0"}%`}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* SIDEBAR - sign-off */}
                <aside className="rounded-2xl bg-white shadow-soft p-6 space-y-6">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-3">
                            Vendor sign-off
                        </div>
                        {signed ? (
                            <div
                                data-testid="vendor-signoff-complete"
                                className="rounded-xl bg-emerald-50 border border-emerald-200 p-5"
                            >
                                <div className="flex items-center gap-2 text-emerald-700 font-medium mb-2">
                                    <CheckCircle2 size={18} />
                                    Signed off
                                </div>
                                <div className="text-sm text-navy-700">
                                    {product.vendor_signed_off_by}
                                </div>
                                <div className="text-xs text-navy-500 mt-1">
                                    {fmtDate(product.vendor_signed_off_at)}
                                </div>
                            </div>
                        ) : isLive ? (
                            <div className="rounded-xl bg-gold-500/10 border border-gold-200 p-5">
                                <div className="flex items-center gap-2 text-gold-700 font-medium mb-2">
                                    <Clock size={18} />
                                    Awaiting your sign-off
                                </div>
                                <p className="text-sm text-navy-700 leading-relaxed">
                                    Perx has published your product to members. Please review the
                                    live listing preview to the left and sign off to confirm
                                    everything is accurate.
                                </p>
                                <Button
                                    data-testid="vendor-signoff-btn"
                                    disabled={signing}
                                    onClick={() => setConfirming(true)}
                                    className="mt-4 w-full rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11"
                                >
                                    <ShieldCheck size={16} className="mr-2" />
                                    {signing ? "Signing…" : "Sign off on this listing"}
                                </Button>
                            </div>
                        ) : (
                            <div className="rounded-xl bg-cream p-5 border border-navy-100/70">
                                <div className="text-sm text-navy-700 leading-relaxed">
                                    {product.status === "Pending Review" && (
                                        <>
                                            <div className="font-medium mb-1">
                                                Waiting on Perx to review this submission.
                                            </div>
                                            <div className="text-xs text-navy-500">
                                                You&apos;ll be able to sign off once we approve the
                                                listing and mark it Live.
                                            </div>
                                        </>
                                    )}
                                    {product.status === "Rejected" && (
                                        <>
                                            <div className="font-medium mb-1 text-red-700">
                                                This submission was rejected.
                                            </div>
                                            <div className="text-xs text-navy-500">
                                                Check the admin review notes for details and
                                                resubmit with the requested changes.
                                            </div>
                                        </>
                                    )}
                                    {product.status === "Draft" && (
                                        <>
                                            <div className="font-medium mb-1">Draft</div>
                                            <div className="text-xs text-navy-500">
                                                Submit this draft to Perx for review.
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="text-xs text-navy-500 pt-4 border-t border-navy-100/70 space-y-1">
                        <div>Submitted {fmtDate(product.submitted_at) || "—"}</div>
                        <div>by {product.submitted_by || "—"}</div>
                        {product.reviewed_by && (
                            <div>Reviewed by {product.reviewed_by}</div>
                        )}
                    </div>
                </aside>
            </div>

            <AlertDialog open={confirming} onOpenChange={setConfirming}>
                <AlertDialogContent className="bg-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Sign off on this listing?</AlertDialogTitle>
                        <AlertDialogDescription>
                            By signing off you confirm the live listing is accurate and that
                            you&apos;re authorised to represent{" "}
                            <strong>{product.vendor}</strong>. This action is recorded
                            against your account and cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="vendor-signoff-cancel">
                            Not yet
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={signOff}
                            data-testid="vendor-signoff-confirm"
                            className="bg-navy-700 hover:bg-navy-800 text-white"
                        >
                            Yes, sign off
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div>
            <div className="flex items-center gap-1.5 text-navy-500 mb-1.5">
                <Icon size={14} strokeWidth={1.7} />
                <span className="text-[10px] uppercase tracking-widest">{label}</span>
            </div>
            <div className="font-display text-2xl text-navy-700">{value}</div>
        </div>
    );
}
